import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Menu, 
  Card, 
  Row, 
  Col, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Table,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Tabs,
  List,
  Avatar,
  Tooltip,
  Badge,
  Divider,
  Alert,
  Dropdown,
  Tree,
  Empty,
  Spin,
  Switch,
  Radio,
  Descriptions,
  Checkbox,
  Popconfirm
} from 'antd';
import { 
  FileTextOutlined, 
  PartitionOutlined, 
  ProjectOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
  FolderOpenOutlined,
  FileMarkdownOutlined,
  BranchesOutlined,
  LinkOutlined,
  TagsOutlined,
  SearchOutlined,
  SyncOutlined,
  AppstoreOutlined,
  BarsOutlined,
  TeamOutlined,
  SettingOutlined,
  ExportOutlined,
  ImportOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BookOutlined,
  ApiOutlined,
  DatabaseOutlined,
  CodeOutlined,
  CloudOutlined,
  SafetyOutlined,
  FundOutlined,
  ApartmentOutlined,
  SwapOutlined,
  CloseOutlined
} from '@ant-design/icons';
import type { MenuProps, TabsProps, TreeDataNode } from 'antd';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import mermaid from 'mermaid';
import MarkdownViewer from './MarkdownViewer';

// API配置
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Mermaid配置
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  themeVariables: {
    primaryColor: '#1890ff',
    primaryTextColor: '#fff',
    primaryBorderColor: '#0050b3',
    lineColor: '#1890ff',
    secondaryColor: '#f0f2f5',
    tertiaryColor: '#fff'
  },
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis'
  }
});

const { Title, Text, Paragraph } = Typography;
const { Sider, Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { DirectoryTree } = Tree;

interface ProcessDocument {
  id: string;
  name: string;
  type: 'document' | 'flowchart' | 'template';
  category: string;
  tags: string[];
  status: 'draft' | 'published' | 'archived';
  lastModified: string;
  author?: string;
  linkedFlows?: string[];
  linkedDocs?: string[];
  content?: string;
  flowData?: any;
  description?: string;
  version?: string;
}

interface FlowTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  preview: string;
  tags: string[];
  usage: number;
}

const ProcessDocumentCenter: React.FC = () => {
  const [selectedMenu, setSelectedMenu] = useState('overview');
  const [documents, setDocuments] = useState<ProcessDocument[]>([]);
  const [flowcharts, setFlowcharts] = useState<any[]>([]);
  const [entityDiagrams, setEntityDiagrams] = useState<any[]>([]);
  const [templates, setTemplates] = useState<FlowTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState<'document' | 'flowchart' | 'from-template'>('document');
  const [selectedItem, setSelectedItem] = useState<ProcessDocument | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDiagram, setPreviewDiagram] = useState<any>(null);
  const [previewZoom, setPreviewZoom] = useState(100);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [sourceDocContent, setSourceDocContent] = useState<string>('');
  const [showSourceDoc, setShowSourceDoc] = useState(false);
  const [batchEditMode, setBatchEditMode] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [batchTargetCategory, setBatchTargetCategory] = useState<string>('general');
  const [batchLoading, setBatchLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<any>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [editLoading, setEditLoading] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // 流程分类
  const processCategories = [
    { key: 'all', label: '全部流程', icon: <AppstoreOutlined /> },
    { key: 'master-data', label: '主数据流程', icon: <DatabaseOutlined /> },
    { key: 'business', label: '业务流程', icon: <FundOutlined /> },
    { key: 'system', label: '系统流程', icon: <ApiOutlined /> },
    { key: 'approval', label: '审批流程', icon: <SafetyOutlined /> },
    { key: 'integration', label: '集成流程', icon: <LinkOutlined /> }
  ];

  // 文档分类
  const documentCategories = [
    { value: 'system_tables', label: '系统表', icon: <DatabaseOutlined /> },
    { value: 'api_docs', label: '接口API', icon: <ApiOutlined /> },
    { value: 'entities', label: '实体', icon: <CodeOutlined /> },
    { value: 'processes', label: '流程', icon: <BranchesOutlined /> },
    { value: 'deployment', label: '部署', icon: <CloudOutlined /> },
    { value: 'configuration', label: '配置', icon: <SettingOutlined /> },
    { value: 'knowledge', label: '知识库', icon: <BookOutlined /> },
    { value: 'general', label: '其他', icon: <FolderOpenOutlined /> }
  ];

  // 流程模板库
  const flowTemplates: FlowTemplate[] = [
    {
      id: '1',
      name: '客户主数据创建流程',
      category: 'master-data',
      description: '标准的客户主数据创建和审批流程',
      preview: 'flowchart TD\n  A[申请创建] --> B[业务审核]\n  B --> C[主数据审核]\n  C --> D[创建完成]',
      tags: ['客户', '主数据', '审批'],
      usage: 156
    },
    {
      id: '2',
      name: '供应商准入流程',
      category: 'business',
      description: '供应商资质审核和准入流程',
      preview: 'flowchart LR\n  A[供应商申请] --> B[资质审核]\n  B --> C[风险评估]\n  C --> D[准入批准]',
      tags: ['供应商', '准入', '风险'],
      usage: 89
    },
    {
      id: '3',
      name: '数据质量检查流程',
      category: 'system',
      description: '主数据质量自动检查和修复流程',
      preview: 'flowchart TD\n  A[数据抽取] --> B[质量检查]\n  B --> C{是否合格}\n  C -->|是| D[入库]\n  C -->|否| E[数据修复]',
      tags: ['数据质量', '自动化'],
      usage: 234
    }
  ];

  useEffect(() => {
    loadAllData();
  }, []);

  // 监听缩放变化，重新渲染图表
  useEffect(() => {
    if (showPreviewModal && previewDiagram) {
      const container = document.querySelector('.mermaid-preview-container') as HTMLDivElement;
      if (container) {
        renderMermaidDiagram(previewDiagram.content, container, previewZoom);
      }
    }
  }, [previewZoom, showPreviewModal, previewDiagram]);

  // 计算Mermaid图中的节点数量
  const countMermaidNodes = (content: string): number => {
    if (!content) return 0;
    
    const nodes = new Set();
    
    // 检查图表类型
    if (content.includes('erDiagram')) {
      // ER图：提取实体名称
      // 1. 从关系定义中提取实体 (A ||--o{ B : 关系)
      const relationPattern = /^\s*([^\s\|\}\{]+)\s*[\|\}][^\:]*\:\s*(.+)/gm;
      const relationMatches = Array.from(content.matchAll(relationPattern));
      for (const match of relationMatches) {
        // 提取关系两端的实体名
        const leftEntity = match[1].trim();
        const rightMatch = match[0].match(/[\|\}][oel\-\|\}]+\{\s*([^\s\:]+)/);
        if (rightMatch) {
          const rightEntity = rightMatch[1].trim();
          nodes.add(leftEntity);
          nodes.add(rightEntity);
        }
      }
      
      // 2. 从实体属性定义中提取实体 (实体名 { ... })
      const entityDefPattern = /^\s*([^\s\|\}\{]+)\s*\{/gm;
      const entityMatches = Array.from(content.matchAll(entityDefPattern));
      for (const match of entityMatches) {
        nodes.add(match[1].trim());
      }
    } else if (content.includes('flowchart') || content.includes('graph')) {
      // 流程图：匹配各种类型的节点定义
      const nodePatterns = [
        /\w+\[.*?\]/g,  // 方括号节点 A[text]
        /\w+\(.*?\)/g,  // 圆括号节点 A(text)
        /\w+\{.*?\}/g,  // 花括号节点 A{text}
        /\w+\[\[.*?\]\]/g,  // 双方括号节点 A[[text]]
        /\w+\(\(.*?\)\)/g,  // 双圆括号节点 A((text))
        /\w+>.*?]/g,     // 不对称节点 A>text]
        /\w+\{\{.*?\}\}/g,  // 双花括号节点 A{{text}}
        /\w+\[\|.*?\|\]/g,  // 子程序节点 A[|text|]
        /\w+\[\\.*?\/\]/g,  // 梯形节点 A[\text/]
        /\w+\[\/.*?\\\]/g,  // 反梯形节点 A[/text\]
      ];
      
      nodePatterns.forEach(pattern => {
        const matches = content.match(pattern) || [];
        matches.forEach(match => {
          const nodeId = match.match(/^\w+/);
          if (nodeId) {
            nodes.add(nodeId[0]);
          }
        });
      });
    } else {
      // 通用方法：尝试匹配所有可能的节点定义
      // 包括flowchart和ER图的模式
      const allPatterns = [
        /\w+\[.*?\]/g,  // 方括号节点
        /\w+\(.*?\)/g,  // 圆括号节点
        /\w+\{.*?\}/g,  // 花括号节点
        /^\s*([A-Z][A-Z0-9_]*)\s*\{/gm,  // ER图实体
      ];
      
      allPatterns.forEach(pattern => {
        const matches = content.matchAll ? Array.from(content.matchAll(pattern)) : (content.match(pattern) || []);
        for (const match of matches) {
          if (typeof match === 'string') {
            const nodeId = match.match(/^\w+/);
            if (nodeId) {
              nodes.add(nodeId[0]);
            }
          } else {
            // matchAll结果
            nodes.add(match[1] || match[0]);
          }
        }
      });
    }
    
    return nodes.size;
  };

  // 根据节点数量计算智能缩放比例
  const calculateSmartZoom = (nodeCount: number): number => {
    // 设计原则：
    // 1. 确保图表适应容器为首要目标
    // 2. 保证文字清晰可读
    // 3. 采用更温和的增长曲线
    
    if (nodeCount <= 3) return 100;    // 3个以内：100%
    if (nodeCount <= 5) return 110;    // 5个节点：110%
    if (nodeCount <= 8) return 120;    // 8个节点：120%
    if (nodeCount <= 10) return 130;   // 10个节点：130%
    if (nodeCount <= 12) return 140;   // 12个节点：140%
    if (nodeCount <= 14) return 150;   // 14个节点：150%
    if (nodeCount <= 16) return 160;   // 16个节点：160%
    if (nodeCount <= 18) return 170;   // 18个节点：170%（组织大流程有17个节点）
    if (nodeCount <= 20) return 180;   // 20个节点：180%
    if (nodeCount <= 25) return 200;   // 25个节点：200%
    if (nodeCount <= 30) return 220;   // 30个节点：220%
    if (nodeCount <= 35) return 240;   // 35个节点：240%
    if (nodeCount <= 40) return 260;   // 40个节点：260%
    if (nodeCount <= 50) return 300;   // 50个节点：300%
    if (nodeCount <= 60) return 350;   // 60个节点：350%
    
    // 超过60个节点，使用公式计算
    return Math.min(500, 350 + (nodeCount - 60) * 2);
  };

  // 渲染Mermaid图表
  const renderMermaidDiagram = async (content: string, container: HTMLDivElement | null, zoom: number = 100) => {
    if (!container) return;
    
    try {
      container.innerHTML = '';
      const id = `mermaid-${Date.now()}`;
      
      // 使用mermaid.render渲染
      const { svg } = await mermaid.render(id, content);
      
      // 创建一个包装器div来应用缩放
      const wrapper = document.createElement('div');
      wrapper.style.transform = `scale(${zoom / 100})`;
      wrapper.style.transformOrigin = 'center center';
      wrapper.style.display = 'inline-block';
      wrapper.innerHTML = svg;
      
      container.innerHTML = '';
      container.appendChild(wrapper);
      
      // 自动调整SVG大小以适应容器
      const svgElement = wrapper.querySelector('svg');
      if (svgElement) {
        // 移除固定的宽高属性
        svgElement.removeAttribute('width');
        svgElement.removeAttribute('height');
        svgElement.style.width = '100%';
        svgElement.style.height = 'auto';
        svgElement.style.maxWidth = 'none';
        
        // 获取viewBox或设置默认值
        const viewBox = svgElement.getAttribute('viewBox');
        if (!viewBox) {
          const bbox = svgElement.getBBox();
          svgElement.setAttribute('viewBox', `0 0 ${bbox.width} ${bbox.height}`);
        }
        
        // 调整字体大小
        const texts = svgElement.querySelectorAll('text');
        texts.forEach(text => {
          const fontSize = parseFloat(window.getComputedStyle(text).fontSize);
          if (fontSize < 14) {
            text.style.fontSize = '14px';
          }
        });
        
        // 调整节点最小尺寸
        const rects = svgElement.querySelectorAll('rect.node');
        rects.forEach(rect => {
          const width = parseFloat(rect.getAttribute('width') || '0');
          if (width < 100) {
            rect.setAttribute('width', '100');
          }
        });
      }
    } catch (error: any) {
      console.error('Mermaid渲染错误:', error);
      if (container) {
        container.innerHTML = `<div style="color: red; padding: 20px; border: 1px solid red; border-radius: 4px;">
          <h4>图表渲染失败</h4>
          <p>错误信息: ${error?.message || '未知错误'}</p>
        </div>`;
      }
    }
  };

  // 加载源文档内容
  const loadSourceDocument = async (docPath: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/markdown/content`, {
        params: { path: docPath }
      });
      return response.data.content || '';
    } catch (error) {
      console.error('加载源文档失败:', error);
      return '';
    }
  };

  // 处理预览
  const handlePreview = async (item: any) => {
    setPreviewDiagram(item);
    setShowSourceDoc(false);
    setSourceDocContent('');
    
    // 如果有源文档，加载源文档内容
    if (item.sourceDoc || item.source_doc) {
      const docPath = item.sourceDoc || item.source_doc;
      const content = await loadSourceDocument(docPath);
      setSourceDocContent(content);
    }
    
    // 如果是Mermaid图表，计算智能缩放比例
    if (item.content && (item.content.includes('mermaid') || item.content.includes('flowchart') || item.content.includes('erDiagram'))) {
      const nodeCount = countMermaidNodes(item.content);
      const initialZoom = calculateSmartZoom(nodeCount);
      console.log(`流程图 "${item.title}" 包含 ${nodeCount} 个节点，设置缩放为 ${initialZoom}%`);
      setPreviewZoom(initialZoom);
      setDragPosition({ x: 0, y: 0 }); // 重置拖拽位置
    } else {
      // 文档类型，使用默认缩放
      setPreviewZoom(100);
      setDragPosition({ x: 0, y: 0 });
    }
    
    setShowPreviewModal(true);
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      // 加载文档
      const docsRes = await axios.get(`${API_BASE_URL}/markdown/files`);
      const docs = (docsRes.data.data?.files || docsRes.data.files || []).map((file: any) => ({
        id: file.id,
        name: file.name,
        type: 'document',
        category: file.category || 'general',
        tags: file.tags || [],
        status: 'published',
        lastModified: file.lastModified || new Date().toISOString(),
        linkedFlows: file.linkedFlows || [],
        content: file.content // 添加内容字段
      }));
      setDocuments(docs);

      // 加载流程图和实体关系图
      const flowsRes = await axios.get(`${API_BASE_URL}/mermaid`);
      const allDiagrams = flowsRes.data || [];
      
      // 根据标题区分流程图和实体关系图
      const flows = allDiagrams.filter((item: any) => 
        item.title && (item.title.includes('流程') || item.title.includes('大流程'))
      );
      const entities = allDiagrams.filter((item: any) => 
        item.title && (item.title.includes('实体') || item.title.includes('关系'))
      );
      
      setFlowcharts(flows);
      setEntityDiagrams(entities);

      // 设置模板
      setTemplates(flowTemplates);
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理编辑文档
  const handleEditDocument = async (doc: any) => {
    try {
      setEditingDoc(doc);
      
      // 如果文档内容不存在，先获取内容
      let content = doc.content;
      if (!content && doc.id) {
        setEditLoading(true);
        try {
          const response = await axios.get(`${API_BASE_URL}/markdown/file/${doc.id}`);
          content = response.data.data?.content || response.data.content || '';
        } catch (error) {
          console.error('获取文档内容失败:', error);
          message.error('获取文档内容失败');
          return;
        } finally {
          setEditLoading(false);
        }
      }
      
      setEditContent(content || '');
      editForm.setFieldsValue({
        fileName: doc.name,
        category: doc.category || 'general',
        content: content || ''
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('打开编辑器失败:', error);
      message.error('打开编辑器失败');
    }
  };

  // 保存编辑的文档
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);

      const response = await axios.put(`${API_BASE_URL}/markdown/save`, {
        id: editingDoc.id,
        fileName: values.fileName,
        category: values.category,
        content: values.content
      });

      if (response.data.success) {
        message.success('文档已更新');
        setShowEditModal(false);
        setEditingDoc(null);
        setEditContent('');
        editForm.resetFields();
        await loadAllData();
      } else {
        message.error(response.data.message || '更新失败');
      }
    } catch (error: any) {
      console.error('保存文档失败:', error);
      message.error(error.response?.data?.message || '保存失败');
    } finally {
      setEditLoading(false);
    }
  };

  // 批量编辑相关方法
  const handleBatchModeToggle = () => {
    setBatchEditMode(!batchEditMode);
    setSelectedDocIds(new Set());
  };

  const handleDocSelect = (docId: string, checked: boolean) => {
    const newSelectedDocs = new Set(selectedDocIds);
    if (checked) {
      newSelectedDocs.add(docId);
    } else {
      newSelectedDocs.delete(docId);
    }
    setSelectedDocIds(newSelectedDocs);
  };

  const handleSelectAll = (checked: boolean) => {
    const filteredDocs = documents.filter(doc => 
      selectedCategory === 'all' || doc.category === selectedCategory
    );
    if (checked) {
      setSelectedDocIds(new Set(filteredDocs.map(doc => doc.id)));
    } else {
      setSelectedDocIds(new Set());
    }
  };

  const handleBatchCategoryChange = async () => {
    if (selectedDocIds.size === 0) {
      message.warning('请先选择要修改的文档');
      return;
    }

    setBatchLoading(true);
    try {
      // 批量更新文档分类
      const response = await axios.put(`${API_BASE_URL}/markdown/batch-update-category`, {
        fileIds: Array.from(selectedDocIds),
        newCategory: batchTargetCategory
      });

      if (response.data.success) {
        const successCount = response.data.results?.success?.length || selectedDocIds.size;
        message.success(`成功将 ${successCount} 个文档的分类标签修改为 ${batchTargetCategory}`);
        
        // 刷新数据
        await loadAllData();
        
        // 退出批量编辑模式
        setBatchEditMode(false);
        setSelectedDocIds(new Set());
      } else {
        message.error(response.data.message || '批量修改分类失败');
      }
    } catch (error) {
      console.error('批量修改分类失败:', error);
      message.error('批量修改分类失败');
    } finally {
      setBatchLoading(false);
    }
  };

  // 创建新内容
  const handleCreate = () => {
    form.validateFields().then(async values => {
      try {
        if (createType === 'document') {
          // 创建文档逻辑
          const fileName = values.name.endsWith('.md') ? values.name : `${values.name}.md`;
          const content = values.content || 
            (values.description ? 
              `# ${values.name}\n\n${values.description}` : 
              `# ${values.name}\n\n请开始编辑您的文档...`);
          
          const response = await axios.post(`${API_BASE_URL}/markdown/create`, {
            fileName: fileName,
            category: values.category || 'general',
            content: content,
            tags: values.tags || []
          });
          
          if (response.data.success) {
            message.success('文档创建成功');
          } else {
            message.error(response.data.message || '文档创建失败');
          }
        } else if (createType === 'flowchart') {
          // 创建流程图
          const response = await axios.post(`${API_BASE_URL}/mermaid`, {
            title: values.name,
            content: values.template || 'flowchart TD\n  A[开始] --> B[结束]',
            source_doc: values.linkedDoc,
            tags: values.tags
          });
          message.success('流程图创建成功');
        } else if (createType === 'from-template') {
          // 从模板创建
          const template = templates.find(t => t.id === values.templateId);
          if (template) {
            const response = await axios.post(`${API_BASE_URL}/mermaid`, {
              title: values.name,
              content: template.preview,
              source_doc: values.linkedDoc,
              tags: [...template.tags, ...values.tags]
            });
            message.success('从模板创建成功');
          }
        }
        setShowCreateModal(false);
        form.resetFields();
        loadAllData();
      } catch (error) {
        message.error('创建失败');
      }
    });
  };

  // 渲染概览页面
  const renderOverview = () => (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <FileTextOutlined style={{ fontSize: 32, color: '#1890ff' }} />
              <Title level={3}>{documents.length}</Title>
              <Text>文档总数</Text>
            </Space>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <PartitionOutlined style={{ fontSize: 32, color: '#52c41a' }} />
              <Title level={3}>{flowcharts.length}</Title>
              <Text>流程图总数</Text>
            </Space>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <LinkOutlined style={{ fontSize: 32, color: '#fa8c16' }} />
              <Title level={3}>
                {documents.filter(d => d.linkedFlows && d.linkedFlows.length > 0).length}
              </Title>
              <Text>已关联文档</Text>
            </Space>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <ProjectOutlined style={{ fontSize: 32, color: '#722ed1' }} />
              <Title level={3}>{templates.length}</Title>
              <Text>流程模板</Text>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="快速开始" extra={
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setCreateType('document');
            setShowCreateModal(true);
          }}>
            新建文档
          </Button>
          <Button icon={<PartitionOutlined />} onClick={() => {
            setCreateType('flowchart');
            setShowCreateModal(true);
          }}>
            新建流程图
          </Button>
        </Space>
      }>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Card hoverable onClick={() => setSelectedMenu('documents')}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <FileMarkdownOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                <Title level={4}>文档管理</Title>
                <Text type="secondary">管理所有项目文档，支持Markdown编辑和分类管理</Text>
              </Space>
            </Card>
          </Col>
          <Col span={8}>
            <Card hoverable onClick={() => setSelectedMenu('flowcharts')}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <BranchesOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                <Title level={4}>流程设计</Title>
                <Text type="secondary">可视化设计业务流程，支持Mermaid语法</Text>
              </Space>
            </Card>
          </Col>
          <Col span={8}>
            <Card hoverable onClick={() => setSelectedMenu('templates')}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <AppstoreOutlined style={{ fontSize: 48, color: '#fa8c16' }} />
                <Title level={4}>模板库</Title>
                <Text type="secondary">预定义的流程模板，快速创建标准流程</Text>
              </Space>
            </Card>
          </Col>
        </Row>
      </Card>

      <Card title="最近更新" style={{ marginTop: 16 }}>
        <List
          dataSource={[...documents, ...flowcharts.map(f => ({
            ...f,
            type: 'flowchart',
            name: f.title
          }))].sort((a, b) => 
            new Date(b.lastModified || b.updated_at).getTime() - 
            new Date(a.lastModified || a.updated_at).getTime()
          ).slice(0, 5)}
          renderItem={item => (
            <List.Item
              actions={[
                <Button type="link" icon={<EyeOutlined />} onClick={() => {
                  setSelectedItem(item as any);
                  setShowDetailModal(true);
                }}>查看</Button>
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar icon={item.type === 'document' ? <FileTextOutlined /> : <PartitionOutlined />} 
                    style={{ backgroundColor: item.type === 'document' ? '#1890ff' : '#52c41a' }}
                  />
                }
                title={item.name || item.title}
                description={
                  <Space>
                    <Tag>{item.type === 'document' ? '文档' : '流程图'}</Tag>
                    <Text type="secondary">
                      {new Date(item.lastModified || item.updated_at).toLocaleString()}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>
    </div>
  );

  // 渲染文档管理
  const renderDocuments = () => {
    const filteredDocs = documents.filter(doc => 
      (selectedCategory === 'all' || doc.category === selectedCategory) &&
      (!searchText || doc.name.toLowerCase().includes(searchText.toLowerCase()))
    );
    
    const isAllSelected = filteredDocs.length > 0 && 
      filteredDocs.every(doc => selectedDocIds.has(doc.id));
    const isIndeterminate = filteredDocs.some(doc => selectedDocIds.has(doc.id)) && !isAllSelected;

    return (
      <div>
        <Card title="文档管理" extra={
          <Space>
            <Input.Search
              placeholder="搜索文档"
              style={{ width: 200 }}
              onSearch={setSearchText}
              disabled={batchEditMode}
            />
            <Select
              placeholder="选择分类"
              style={{ width: 150 }}
              value={selectedCategory}
              onChange={setSelectedCategory}
              disabled={batchEditMode}
            >
              <Option value="all">全部分类</Option>
              {documentCategories.map(cat => (
                <Option key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </Option>
              ))}
            </Select>
            <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} disabled={batchEditMode}>
              <Radio.Button value="grid"><AppstoreOutlined /></Radio.Button>
              <Radio.Button value="list"><BarsOutlined /></Radio.Button>
            </Radio.Group>
            <Button 
              type={batchEditMode ? "primary" : "default"}
              icon={batchEditMode ? <CloseOutlined /> : <SwapOutlined />}
              onClick={handleBatchModeToggle}
            >
              {batchEditMode ? '退出批量编辑' : '批量编辑'}
            </Button>
            {!batchEditMode && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                setCreateType('document');
                setShowCreateModal(true);
              }}>
                新建文档
              </Button>
            )}
          </Space>
        }>
          {/* 批量编辑工具栏 */}
          {batchEditMode && (
            <div style={{ marginBottom: 16 }}>
              <Alert
                type="info"
                showIcon
                message={
                  <Space>
                    <span>批量编辑模式：已选择 {selectedDocIds.size} 个文档</span>
                    {filteredDocs.length > 0 && (
                      <Checkbox
                        indeterminate={isIndeterminate}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        checked={isAllSelected}
                      >
                        全选当前分类
                      </Checkbox>
                    )}
                  </Space>
                }
                style={{ marginBottom: 12 }}
              />
              {selectedDocIds.size > 0 && (
                <div style={{ 
                  padding: 12, 
                  background: '#e6f7ff', 
                  borderRadius: 6,
                  border: '1px solid #91d5ff'
                }}>
                  <Space align="center">
                    <Text>将选中的 {selectedDocIds.size} 个文档移动到：</Text>
                    <Select
                      value={batchTargetCategory}
                      onChange={setBatchTargetCategory}
                      style={{ width: 150 }}
                    >
                      {documentCategories.map(cat => (
                        <Option key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </Option>
                      ))}
                    </Select>
                    <Button 
                      type="primary"
                      onClick={handleBatchCategoryChange}
                      loading={batchLoading}
                    >
                      确认修改
                    </Button>
                  </Space>
                </div>
              )}
            </div>
          )}

          {viewMode === 'grid' ? (
          <Row gutter={[16, 16]}>
            {documents
              .filter(doc => 
                (selectedCategory === 'all' || doc.category === selectedCategory) &&
                (!searchText || doc.name.toLowerCase().includes(searchText.toLowerCase()))
              )
              .map(doc => (
                <Col key={doc.id} span={6}>
                  <Card
                    hoverable={!batchEditMode}
                    style={batchEditMode && selectedDocIds.has(doc.id) ? { 
                      border: '2px solid #1890ff',
                      background: '#e6f7ff'
                    } : {}}
                    actions={batchEditMode ? [
                      <Checkbox
                        checked={selectedDocIds.has(doc.id)}
                        onChange={(e) => handleDocSelect(doc.id, e.target.checked)}
                      >
                        选择
                      </Checkbox>
                    ] : [
                      <Tooltip title="查看"><EyeOutlined onClick={() => {
                        setSelectedItem(doc);
                        setShowDetailModal(true);
                      }} /></Tooltip>,
                      <Tooltip title="编辑"><EditOutlined onClick={() => handleEditDocument(doc)} /></Tooltip>,
                      <Tooltip title="关联流程">
                        <LinkOutlined />
                        {doc.linkedFlows && doc.linkedFlows.length > 0 && (
                          <Badge count={doc.linkedFlows.length} style={{ marginLeft: 4 }} />
                        )}
                      </Tooltip>
                    ]}
                  >
                    <Card.Meta
                      avatar={<FileMarkdownOutlined style={{ fontSize: 32, color: '#1890ff' }} />}
                      title={doc.name}
                      description={
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Tag color="blue">{documentCategories.find(c => c.value === doc.category)?.label || '其他'}</Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(doc.lastModified).toLocaleDateString()}
                          </Text>
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              ))}
          </Row>
        ) : (
          <Table
            dataSource={filteredDocs}
            rowSelection={batchEditMode ? {
              selectedRowKeys: Array.from(selectedDocIds),
              onChange: (selectedRowKeys) => {
                setSelectedDocIds(new Set(selectedRowKeys as string[]));
              }
            } : undefined}
            columns={[
              {
                title: '文档名称',
                dataIndex: 'name',
                key: 'name',
                render: (text, record) => (
                  <Space>
                    <FileMarkdownOutlined />
                    <a onClick={() => {
                      setSelectedItem(record);
                      setShowDetailModal(true);
                    }}>{text}</a>
                  </Space>
                )
              },
              {
                title: '分类',
                dataIndex: 'category',
                key: 'category',
                render: category => (
                  <Tag color="blue">
                    {documentCategories.find(c => c.value === category)?.label || '其他'}
                  </Tag>
                )
              },
              {
                title: '关联流程',
                dataIndex: 'linkedFlows',
                key: 'linkedFlows',
                render: flows => flows && flows.length > 0 ? (
                  <Badge count={flows.length} />
                ) : '-'
              },
              {
                title: '最后修改',
                dataIndex: 'lastModified',
                key: 'lastModified',
                render: date => new Date(date).toLocaleDateString()
              },
              {
                title: '操作',
                key: 'action',
                render: (_, record) => (
                  <Space>
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => {
                      setSelectedItem(record);
                      setShowDetailModal(true);
                    }}>查看</Button>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditDocument(record)}>编辑</Button>
                    <Button type="link" size="small" icon={<LinkOutlined />}>关联</Button>
                  </Space>
                )
              }
            ]}
            rowKey="id"
          />
        )}
        </Card>
      </div>
    );
  };

  // 渲染流程图管理
  const renderFlowcharts = () => (
    <div>
      <Tabs defaultActiveKey="flows">
        <TabPane tab={`流程图 (${flowcharts.length})`} key="flows">
          <Card extra={
            <Space>
              <Input.Search
                placeholder="搜索流程图"
                style={{ width: 200 }}
              />
              <Select
                placeholder="选择分类"
                style={{ width: 150 }}
                defaultValue="all"
              >
                {processCategories.map(cat => (
                  <Option key={cat.key} value={cat.key}>
                    {cat.icon} {cat.label}
                  </Option>
                ))}
              </Select>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                setCreateType('flowchart');
                setShowCreateModal(true);
              }}>
                新建流程图
              </Button>
            </Space>
          }>
            <Row gutter={[16, 16]}>
              {flowcharts.map(flow => (
                <Col key={flow.id} span={8}>
                  <Card
                    hoverable
                    cover={
                      <div style={{ 
                        height: 150, 
                        padding: 16, 
                        background: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PartitionOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                      </div>
                    }
                    actions={[
                      <Tooltip title="查看"><EyeOutlined onClick={() => handlePreview(flow)} /></Tooltip>,
                      <Tooltip title="编辑"><EditOutlined /></Tooltip>,
                      <Tooltip title="导出"><ExportOutlined /></Tooltip>
                    ]}
                  >
                    <Card.Meta
                      title={flow.title}
                      description={
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {flow.source_doc && (
                            <Space>
                              <LinkOutlined />
                              <Text type="secondary">已关联文档</Text>
                            </Space>
                          )}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            更新于 {new Date(flow.updated_at).toLocaleDateString()}
                          </Text>
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </TabPane>
        
        <TabPane tab={`实体关系图 (${entityDiagrams.length})`} key="entities">
          <Card extra={
            <Space>
              <Input.Search
                placeholder="搜索实体关系图"
                style={{ width: 200 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                setCreateType('flowchart');
                setShowCreateModal(true);
              }}>
                新建实体关系图
              </Button>
            </Space>
          }>
            <Row gutter={[16, 16]}>
              {entityDiagrams.map(diagram => (
                <Col key={diagram.id} span={8}>
                  <Card
                    hoverable
                    cover={
                      <div style={{ 
                        height: 150, 
                        padding: 16, 
                        background: '#f0f5ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <ApartmentOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                      </div>
                    }
                    actions={[
                      <Tooltip title="查看"><EyeOutlined onClick={() => handlePreview(diagram)} /></Tooltip>,
                      <Tooltip title="编辑"><EditOutlined /></Tooltip>,
                      <Tooltip title="导出"><ExportOutlined /></Tooltip>
                    ]}
                  >
                    <Card.Meta
                      title={diagram.title}
                      description={
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {diagram.source_doc && (
                            <Space>
                              <LinkOutlined />
                              <Text type="secondary">已关联文档</Text>
                            </Space>
                          )}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            更新于 {new Date(diagram.updated_at).toLocaleDateString()}
                          </Text>
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );

  // 渲染模板库
  const renderTemplates = () => (
    <div>
      <Card title="流程模板库">
        <Tabs defaultActiveKey="all">
          <TabPane tab="全部模板" key="all">
            <Row gutter={[16, 16]}>
              {templates.map(template => (
                <Col key={template.id} span={8}>
                  <Card
                    hoverable
                    actions={[
                      <Button type="link" icon={<EyeOutlined />}>预览</Button>,
                      <Button type="link" icon={<CopyOutlined />} onClick={() => {
                        setCreateType('from-template');
                        form.setFieldsValue({ templateId: template.id });
                        setShowCreateModal(true);
                      }}>使用模板</Button>
                    ]}
                  >
                    <Card.Meta
                      avatar={<ProjectOutlined style={{ fontSize: 32, color: '#722ed1' }} />}
                      title={template.name}
                      description={
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Paragraph ellipsis={{ rows: 2 }}>
                            {template.description}
                          </Paragraph>
                          <Space>
                            {template.tags.map(tag => (
                              <Tag key={tag}>{tag}</Tag>
                            ))}
                          </Space>
                          <Space>
                            <Text type="secondary">使用次数: {template.usage}</Text>
                          </Space>
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </TabPane>
          <TabPane tab="主数据流程" key="master-data">
            <Row gutter={[16, 16]}>
              {templates.filter(t => t.category === 'master-data').map(template => (
                <Col key={template.id} span={8}>
                  <Card>
                    <Card.Meta title={template.name} description={template.description} />
                  </Card>
                </Col>
              ))}
            </Row>
          </TabPane>
          <TabPane tab="业务流程" key="business">
            <Row gutter={[16, 16]}>
              {templates.filter(t => t.category === 'business').map(template => (
                <Col key={template.id} span={8}>
                  <Card>
                    <Card.Meta title={template.name} description={template.description} />
                  </Card>
                </Col>
              ))}
            </Row>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );

  // 渲染关联管理
  const renderRelations = () => (
    <div>
      <Card title="文档与流程关联管理">
        <Alert
          message="通过关联管理，您可以将相关的文档和流程图建立联系，便于追溯和理解业务逻辑"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Row gutter={16}>
          <Col span={12}>
            <Card title="已关联的文档" type="inner">
              <List
                dataSource={documents.filter(d => d.linkedFlows && d.linkedFlows.length > 0)}
                renderItem={doc => (
                  <List.Item
                    actions={[
                      <Button type="link" size="small">管理关联</Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileTextOutlined />}
                      title={doc.name}
                      description={`关联了 ${doc.linkedFlows?.length || 0} 个流程图`}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="待关联的文档" type="inner">
              <List
                dataSource={documents.filter(d => !d.linkedFlows || d.linkedFlows.length === 0).slice(0, 5)}
                renderItem={doc => (
                  <List.Item
                    actions={[
                      <Button type="link" size="small">建立关联</Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileTextOutlined />}
                      title={doc.name}
                      description="未关联流程图"
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );

  const menuItems: MenuProps['items'] = [
    {
      key: 'overview',
      icon: <AppstoreOutlined />,
      label: '总览'
    },
    {
      key: 'documents',
      icon: <FileTextOutlined />,
      label: '文档管理'
    },
    {
      key: 'flowcharts',
      icon: <PartitionOutlined />,
      label: '流程图管理'
    },
    {
      key: 'templates',
      icon: <ProjectOutlined />,
      label: '模板库'
    },
    {
      key: 'relations',
      icon: <LinkOutlined />,
      label: '关联管理'
    }
  ];

  return (
    <Layout style={{ height: '100%' }}>
      <Sider width={200} style={{ background: '#fff' }}>
        <Menu
          mode="inline"
          selectedKeys={[selectedMenu]}
          items={menuItems}
          onClick={({ key }) => setSelectedMenu(key)}
        />
      </Sider>
      <Content style={{ padding: 24, minHeight: 280 }}>
        <Spin spinning={loading}>
          {selectedMenu === 'overview' && renderOverview()}
          {selectedMenu === 'documents' && renderDocuments()}
          {selectedMenu === 'flowcharts' && renderFlowcharts()}
          {selectedMenu === 'templates' && renderTemplates()}
          {selectedMenu === 'relations' && renderRelations()}
        </Spin>
      </Content>

      {/* 创建模态框 */}
      <Modal
        title={
          <Space>
            {createType === 'document' ? <FileMarkdownOutlined /> : 
             createType === 'flowchart' ? <PartitionOutlined /> : 
             <AppstoreOutlined />}
            {createType === 'document' ? '新建文档' : 
             createType === 'flowchart' ? '新建流程图' : 
             '从模板创建'}
          </Space>
        }
        open={showCreateModal}
        onOk={handleCreate}
        onCancel={() => {
          setShowCreateModal(false);
          form.resetFields();
        }}
        width={720}
        okText="创建"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder={`请输入${createType === 'document' ? '文档' : '流程图'}名称`} />
          </Form.Item>
          
          {createType === 'from-template' && (
            <Form.Item
              name="templateId"
              label="选择模板"
              rules={[{ required: true, message: '请选择模板' }]}
            >
              <Select placeholder="请选择模板">
                {templates.map(t => (
                  <Option key={t.id} value={t.id}>{t.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}
          
          <Form.Item
            name="category"
            label="分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select placeholder="请选择分类">
              {createType === 'document' ? 
                documentCategories.map(cat => (
                  <Option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </Option>
                )) :
                processCategories.filter(cat => cat.key !== 'all').map(cat => (
                  <Option key={cat.key} value={cat.key}>
                    {cat.icon} {cat.label}
                  </Option>
                ))
              }
            </Select>
          </Form.Item>
          
          <Form.Item 
            name="tags" 
            label="标签"
            tooltip="可以输入自定义标签，按回车确认"
          >
            <Select 
              mode="tags" 
              placeholder="选择或输入标签，按回车添加"
              style={{ width: '100%' }}
            >
              <Option value="主数据">主数据</Option>
              <Option value="业务流程">业务流程</Option>
              <Option value="系统集成">系统集成</Option>
              <Option value="API文档">API文档</Option>
              <Option value="数据模型">数据模型</Option>
              <Option value="技术方案">技术方案</Option>
              <Option value="需求文档">需求文档</Option>
              <Option value="设计文档">设计文档</Option>
              <Option value="测试文档">测试文档</Option>
              <Option value="部署文档">部署文档</Option>
              <Option value="用户手册">用户手册</Option>
              <Option value="运维文档">运维文档</Option>
            </Select>
          </Form.Item>
          
          {createType === 'flowchart' && (
            <Form.Item name="linkedDoc" label="关联文档">
              <Select placeholder="选择要关联的文档（可选）">
                {documents.map(doc => (
                  <Option key={doc.id} value={doc.id}>{doc.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}
          
          {createType === 'document' && (
            <>
              <Form.Item 
                name="description" 
                label="文档描述"
                tooltip="简要描述文档的用途和主要内容"
              >
                <TextArea 
                  rows={2} 
                  placeholder="请简要描述文档的用途和主要内容" 
                />
              </Form.Item>
              
              <Form.Item 
                name="content" 
                label="初始内容 (Markdown格式)"
                tooltip="支持Markdown语法，可以使用标题、列表、代码块等格式"
              >
                <TextArea 
                  rows={10} 
                  placeholder="请输入文档的初始内容，支持Markdown格式...&#10;&#10;示例：&#10;## 概述&#10;这是文档的概述部分&#10;&#10;## 详细内容&#10;- 列表项1&#10;- 列表项2" 
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </>
          )}
          
          {createType !== 'document' && (
            <Form.Item name="description" label="描述">
              <TextArea rows={3} placeholder="请输入描述信息" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title={selectedItem?.name || (selectedItem as any)?.title}
        open={showDetailModal}
        onCancel={() => {
          setShowDetailModal(false);
          setSelectedItem(null);
        }}
        footer={[
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => {
              handleEditDocument(selectedItem);
              setShowDetailModal(false);
            }}
          >
            编辑
          </Button>,
          <Button key="close" onClick={() => setShowDetailModal(false)}>
            关闭
          </Button>
        ]}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(90vh - 108px)', overflow: 'auto' }}
      >
        {selectedItem && (
          <div>
            <Descriptions bordered>
              <Descriptions.Item label="类型">
                {selectedItem.type === 'document' ? '文档' : '流程图'}
              </Descriptions.Item>
              <Descriptions.Item label="分类">
                {selectedItem.category}
              </Descriptions.Item>
              <Descriptions.Item label="最后修改">
                {new Date(selectedItem.lastModified || (selectedItem as any).updated_at).toLocaleString()}
              </Descriptions.Item>
              {selectedItem.linkedFlows && (
                <Descriptions.Item label="关联流程" span={3}>
                  {selectedItem.linkedFlows.length > 0 ? 
                    selectedItem.linkedFlows.join(', ') : '无'
                  }
                </Descriptions.Item>
              )}
            </Descriptions>
            
            {selectedItem.content && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>内容预览</Title>
                <div style={{ 
                  height: 'calc(70vh - 250px)', 
                  minHeight: 400,
                  overflow: 'auto', 
                  border: '1px solid #e8e8e8',
                  borderRadius: 4
                }}>
                  <MarkdownViewer 
                    content={selectedItem.content}
                    fileName={selectedItem.name || 'document.md'}
                    showToc={true}
                    containerStyle={{ height: '100%' }}
                    style={{ padding: 20 }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 内容预览模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              {previewDiagram && previewDiagram.content && (previewDiagram.content.includes('mermaid') || previewDiagram.content.includes('flowchart') || previewDiagram.content.includes('erDiagram')) ? (
                <>
                  <PartitionOutlined />
                  <span>{previewDiagram?.title || '流程图预览'}</span>
                </>
              ) : (
                <>
                  <FileTextOutlined />
                  <span>{previewDiagram?.title || previewDiagram?.name || '文档预览'}</span>
                </>
              )}
            </Space>
            {/* 只有Mermaid图表才显示缩放控制 */}
            {previewDiagram && previewDiagram.content && (previewDiagram.content.includes('mermaid') || previewDiagram.content.includes('flowchart') || previewDiagram.content.includes('erDiagram')) && (
              <Space>
                <Button 
                  size="small" 
                  onClick={() => setPreviewZoom(Math.max(50, previewZoom - 25))}
                  disabled={previewZoom <= 50}
                >
                  缩小
                </Button>
                <span style={{ minWidth: 60, textAlign: 'center' }}>{previewZoom}%</span>
                <Button 
                  size="small" 
                  onClick={() => setPreviewZoom(Math.min(1000, previewZoom + 25))}
                  disabled={previewZoom >= 1000}
                >
                  放大
                </Button>
                <Button 
                  size="small" 
                  onClick={() => {
                    const nodeCount = countMermaidNodes(previewDiagram?.content || '');
                    const initialZoom = calculateSmartZoom(nodeCount);
                    setPreviewZoom(initialZoom);
                  }}
                >
                  重置
                </Button>
              </Space>
            )}
          </div>
        }
        open={showPreviewModal}
        onCancel={() => {
          setShowPreviewModal(false);
          setPreviewDiagram(null);
          setPreviewZoom(100);
          setDragPosition({ x: 0, y: 0 });
        }}
        width="95%"
        style={{ top: 20 }}
        footer={[
          sourceDocContent && (
            <Button 
              key="toggle-doc" 
              type={showSourceDoc ? 'primary' : 'default'}
              icon={<FileTextOutlined />}
              onClick={() => setShowSourceDoc(!showSourceDoc)}
            >
              {showSourceDoc ? '查看流程图' : '查看源文档'}
            </Button>
          ),
          <Button key="close" onClick={() => {
            setShowPreviewModal(false);
            setPreviewZoom(100);
            setDragPosition({ x: 0, y: 0 });
            setShowSourceDoc(false);
            setSourceDocContent('');
          }}>
            关闭
          </Button>
        ].filter(Boolean)}
      >
        {previewDiagram && (
          <div style={{ height: '75vh' }}>
            {/* 如果显示源文档 */}
            {showSourceDoc && sourceDocContent ? (
              <div style={{ height: '100%', overflow: 'auto', background: '#fff' }}>
                <MarkdownViewer 
                  content={sourceDocContent} 
                  fileName={(previewDiagram.sourceDoc || previewDiagram.source_doc || 'source.md').split('/').pop()}
                  showToc={true}
                />
              </div>
            ) : /* 判断是Mermaid图表还是文档 */
            previewDiagram.content && (previewDiagram.content.includes('mermaid') || previewDiagram.content.includes('flowchart') || previewDiagram.content.includes('erDiagram')) ? (
              // Mermaid图表预览
              <div 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  overflow: 'hidden',
                  background: '#f5f5f5',
                  borderRadius: 4,
                  position: 'relative',
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onMouseDown={(e) => {
                  if (e.button === 0) { // 左键
                    setIsDragging(true);
                    setDragStart({ x: e.clientX - dragPosition.x, y: e.clientY - dragPosition.y });
                  }
                }}
                onMouseMove={(e) => {
                  if (isDragging) {
                    setDragPosition({
                      x: e.clientX - dragStart.x,
                      y: e.clientY - dragStart.y
                    });
                  }
                }}
                onMouseUp={() => setIsDragging(false)}
                onMouseLeave={() => setIsDragging(false)}
              >
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    padding: '20px'
                  }}
                >
                  <div 
                    className="mermaid-preview-container"
                    style={{
                      display: 'inline-block',
                      transform: `scale(${previewZoom / 100}) translate(${dragPosition.x}px, ${dragPosition.y}px)`,
                      transformOrigin: 'center center',
                      transition: isDragging ? 'none' : 'transform 0.2s'
                    }}
                  />
                </div>
              </div>
            ) : (
              // 文档预览 
              <div style={{ height: '100%', overflow: 'auto', background: '#fff' }}>
                {previewDiagram.content ? (
                  <MarkdownViewer 
                    content={previewDiagram.content} 
                    fileName={previewDiagram.name || previewDiagram.title || 'document.md'}
                    showToc={true}
                  />
                ) : (
                  <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                    <FileTextOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                    <div>该文档没有内容</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 编辑文档模态框 */}
      <Modal
        title={`编辑文档: ${editingDoc?.name || ''}`}
        open={showEditModal}
        onCancel={() => {
          setShowEditModal(false);
          setEditingDoc(null);
          setEditContent('');
          editForm.resetFields();
        }}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              setShowEditModal(false);
              setEditingDoc(null);
              setEditContent('');
              editForm.resetFields();
            }}
          >
            取消
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            loading={editLoading}
            onClick={handleSaveEdit}
          >
            保存
          </Button>
        ]}
      >
        <Form form={editForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="文档名称"
                name="fileName"
                rules={[{ required: true, message: '请输入文档名称' }]}
              >
                <Input placeholder="请输入文档名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="文档分类"
                name="category"
                rules={[{ required: true, message: '请选择文档分类' }]}
              >
                <Select placeholder="请选择文档分类">
                  <Select.Option value="system_tables">系统表</Select.Option>
                  <Select.Option value="api_docs">接口API</Select.Option>
                  <Select.Option value="entities">实体</Select.Option>
                  <Select.Option value="processes">流程</Select.Option>
                  <Select.Option value="deployment">部署</Select.Option>
                  <Select.Option value="configuration">配置</Select.Option>
                  <Select.Option value="knowledge">知识库</Select.Option>
                  <Select.Option value="general">其他</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            label="文档内容 (Markdown格式)"
            name="content"
            rules={[{ required: true, message: '请输入文档内容' }]}
          >
            <Input.TextArea 
              rows={20}
              placeholder="请输入Markdown格式的文档内容..."
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default ProcessDocumentCenter;